/**
 * useCrossViewerSync — cross-viewer marker sync (Phase 1a).
 *
 * A single singleton watcher on the reactive `popupCardState`. When a CML/TML
 * popup loads, it orients (zoom/rotate) and highlights the SAME entity's marker
 * in the OTHER currently-visible viewers (3D + 360), if a marker exists there.
 * It never opens/closes popups — it only observes popup state and drives the
 * viewers. Imported once (side-effect) by MainView.vue so the watcher lives.
 *
 * Design + audit: .claude/features/CROSS_VIEWER_MARKER_SYNC.md (v2).
 * Mirrors the singleton `_hostVue` watcher pattern in usePopupCard.js.
 *
 * Coordinate space (load-bearing):
 *   - 3D  → viewer space: the matched marker's `_basePosition`.
 *   - 360 → raw/world:    `vm.raw.point` (zoomToPointIn360Viewer coerces + subtracts forgeCameraPosition).
 */

import Vue from 'vue';
import store from '@/store';
import { popupCardState } from '@/composables/usePopupCard';
import { highlightLinkElement, ensureDocLinkVisible, openAttachment } from '@/scripts/js/forgeviewer';
import documentsService from '@/features/Task/service/documents.service';
import { GET_DOCUMENT_BY_ID } from '@/utils/attachmentsAPI';
import { getDocumentId } from '@/utils/documentProxyUtils';
import { useSpatialFocus } from '@/features/Dashboard/composables/useSpatialFocus';
import { log as debugLog } from '@/utils/debugLogger';

// { entityType, entityId, rawPoint } currently highlighted (for scan-load re-assert).
let _active = null;
const DOC_HIGHLIGHT_RETRY_MS = 250;
const DOC_HIGHLIGHT_MAX_TICKS = 60;

/**
 * One scene owner for every CML/TmlPoint surface (row, tree, list, kebab, popup, marker click), so
 * they supersede each other through the composable's per-viewer generation instead of fighting,
 * and so a sibling widget cannot blank the scene one of them published.
 */
const CML_FOCUS_OWNER_ID = 'cml-entity-focus';

/** Popup entity type -> the server's stable subject kind. */
const ENTITY_SCOPE_KIND = { cml: 'cml', tmlpoint: 'tml' };

function _fs() {
  return store.state.forgeService || null;
}

/** Idempotent — remove any highlight we placed in 3D, 360, and the document. */
export function clearSync() {
  const fs = _fs();
  if (fs) {
    if (fs.clearMarkerHighlightIn3D) fs.clearMarkerHighlightIn3D();
    if (fs.clearPointHighlightIn360) fs.clearPointHighlightIn360();
  }
  clearDocHighlight();
  _active = null;
}

// Remove any doc-viewer link highlight we applied (mirrors highlightLinkElement's
// own clear pass — class + CSS-var driven, so this fully clears the pulse).
function clearDocHighlight() {
  try {
    document
      .querySelectorAll('.markup-link.highlighted-link, .markup-link-popup.highlighted-link, .v7-annotation-box.highlighted-link')
      .forEach((el) => {
        el.classList.remove('highlighted-link', 'link-flash');
        el.style.removeProperty('--flash-color');
        el.style.removeProperty('--flash-color-light');
        el.style.removeProperty('opacity'); // revert the Focus-Mode-override set on highlight
      });
  } catch (err) {
    debugLog('[xsync] doc highlight clear skipped', err);
  }
}

/**
 * Doc-as-target for a SELECTED CML/TML: if the entity's markup link is on the
 * CURRENTLY-OPEN document's active page, pulse-highlight it in place (ripple via
 * highlightLinkElement) and return true. Returns false when there's no open doc
 * or the active page doesn't carry the link — focusCmlDoc then opens the linked
 * document (CML only).
 */
function syncDocHighlight(entityType, entityId, colorHex) {
  if (!store.state.pdfViewerLoaded || !store.state.pdfviewer) return false;
  const att = store.getters['getActiveAttachment'];
  const pageId = store.getters['getActiveAttachmentPage'];
  const page = att && att.pagesMarkups ? att.pagesMarkups.find((o) => o.pageId == pageId) : null;
  if (!page || !page.links) return false;
  const link = page.links.find((l) => _linkMatchesEntity(l, entityType, entityId));
  if (!link) return false;
  // Null the tagId so highlightLinkElement matches the EXACT link by data-id.
  // Its matcher is `elId === linkId || elTagId === tagId` with last-match-wins;
  // many CML links share one master tagId (data-metadata), so the real tagId
  // would land on the last sibling under that master, not this CML/TML.
  // Pass the condition color so the doc highlight matches the 3D/360 markers.
  highlightLinkElement({ ...link, tagId: null }, store.state.pdfviewer, colorHex);
  ensureDocLinkVisible(link, store.state.pdfviewer);
  debugLog('[xsync] doc link highlighted', entityType, entityId, 'linkId=', link.id);
  return link;
}

// The cmlId/tmlPointId lives on the link or in its metadata JSON; data-metadata
// (= tagId) is too coarse to tell two CMLs on one master tag apart, so match here.
function _linkMatchesEntity(l, entityType, entityId) {
  if (!l) return false;
  let meta = {};
  try { meta = l.metadata ? JSON.parse(l.metadata) : {}; } catch (err) { meta = {}; }
  if (entityType === 'tmlpoint') return String(l.tmlPointId || meta.tmlPointId) === String(entityId);
  return String(l.cmlId || meta.cmlId) === String(entityId);
}

function _activePageEntityLink(entityType, entityId, targetDocumentId) {
  const att = store.getters['getActiveAttachment'];
  if (!att || !att.pagesMarkups) return { parsed: false, link: null };
  if (targetDocumentId != null && String(getDocumentId(att)) !== String(targetDocumentId)) {
    return { parsed: false, link: null };
  }
  const pageId = store.getters['getActiveAttachmentPage'];
  const page = att.pagesMarkups.find((item) => item.pageId == pageId);
  if (!page || !Array.isArray(page.links)) return { parsed: false, link: null };
  return {
    parsed: true,
    link: page.links.find((item) => _linkMatchesEntity(item, entityType, entityId)) || null,
  };
}

function _isExactDocLinkHighlighted(link) {
  if (!link) return false;
  const viewer = store.state.pdfviewer;
  const selector = '.markup-link.highlighted-link, .markup-link-popup.highlighted-link, .v7-annotation-box.highlighted-link';
  let highlighted = viewer && viewer.container
    ? viewer.container.querySelectorAll(selector)
    : [];
  if ((!highlighted || highlighted.length === 0) && typeof document !== 'undefined') {
    highlighted = document.querySelectorAll(selector);
  }
  const targetId = String(link.id || link.linkedAttachmentId || '');
  return Array.from(highlighted || []).some((element) => {
    const elementId = String(element.getAttribute('data-id') || element.id || '');
    return elementId === targetId;
  });
}

function _activeDocumentChanged(targetDocumentId) {
  const activeAttachment = store.getters['getActiveAttachment'];
  return !!activeAttachment && targetDocumentId != null
    && String(getDocumentId(activeAttachment)) !== String(targetDocumentId);
}

function _syncDocHighlightSafely(request) {
  try {
    return syncDocHighlight(request.entityType, request.entityId, request.colorHex);
  } catch (err) {
    debugLog('[xsync] post-open doc highlight skipped', err);
    return null;
  }
}

function _scheduleDocHighlightRetry(request) {
  setTimeout(() => _tryDocEntityHighlight(request), DOC_HIGHLIGHT_RETRY_MS);
}

function _tryDocEntityHighlight(request) {
  if (request.cancelled || request.isStale()) return;
  request.ticks += 1;
  if (_activeDocumentChanged(request.targetDocumentId)) {
    debugLog('[xsync] post-open doc highlight cancelled: active document changed', request.entityId);
    return;
  }

  const highlightedLink = _syncDocHighlightSafely(request);
  if (_isExactDocLinkHighlighted(highlightedLink)) {
    debugLog('[xsync] post-open doc highlight landed', request.entityType, request.entityId, `(tick ${request.ticks})`);
    return;
  }

  const pageState = _activePageEntityLink(request.entityType, request.entityId, request.targetDocumentId);
  if ((pageState.parsed && !pageState.link) || request.ticks >= DOC_HIGHLIGHT_MAX_TICKS) {
    debugLog('[xsync] post-open doc highlight gave up', request.entityType, request.entityId, `(parsed=${pageState.parsed}, linkOnPage=${!!pageState.link}, ticks=${request.ticks})`);
    return;
  }
  _scheduleDocHighlightRetry(request);
}

/**
 * Re-assert an exact CML/TML document highlight until its link DOM has rendered.
 * The PDF data, viewer-loaded flag, and visual link nodes become ready at different
 * times, so success is the target link's actual highlighted DOM node, not data alone.
 */
export function assertDocEntityHighlightWhenRendered({
  entityType,
  entityId,
  colorHex,
  targetDocumentId,
  isStale = () => false,
}) {
  const request = {
    entityType,
    entityId,
    colorHex,
    targetDocumentId,
    isStale,
    ticks: 0,
    cancelled: false,
  };
  Vue.nextTick(() => _scheduleDocHighlightRetry(request));
  return () => { request.cancelled = true; };
}

// Doc-as-target for a SELECTED CML (requirement: "in the document view, open the
// associated document"): highlight the link in the open doc if present, else open
// the CML-linked document and highlight once it loads. TMLs get highlight-only
// (their link/open path resolves through the parent CML — out of scope here).
export function focusCmlDoc(entityType, entityId, colorHex, allowSwap) {
  // Only act when a document viewer is ALREADY in use — never auto-create a panel.
  if (!store.state.pdfViewerLoaded || !store.state.pdfviewer) return;
  // Highlight the CML's link in place if the open doc carries it.
  if (syncDocHighlight(entityType, entityId, colorHex)) return;
  // Otherwise swap the OPEN viewer to the CML-linked document — only for explicit
  // row/kebab focus selections (allowSwap). Marker clicks pass allowSwap=false and
  // keep their audited highlight-only behavior.
  if (entityType !== 'cml' || !allowSwap) return;
  openCmlLinkedDocument(entityId, colorHex);
}

// Fetch the CML's linked documents (markup-link scoped) and open the first in the
// doc viewer — mirrors CmlPopupActionBar.openDoc. Best-effort + fire-and-forget;
// skips the open when the linked doc is already the active attachment (the
// in-place highlight handles that). Re-asserts the precise highlight after load.
async function openCmlLinkedDocument(cmlId, colorHex) {
  // The selection can move to a DIFFERENT CML while these awaits are in flight, and there
  // is no AbortController on the store dispatches — so re-check the live selection after
  // every await and bail if it changed, else a stale chain swaps to the WRONG CML's doc.
  // openCmlLinkedDocument is only reached via a row/kebab focus (allowSwap=true); the
  // durable selection is TAGS.selectedCmlId, set by focusCml before this runs.
  const isStale = () => String(store.state.TAGS && store.state.TAGS.selectedCmlId) !== String(cmlId);
  try {
    const res = await documentsService.getByCmlId(cmlId, { AssetId: store.state.assetId });
    if (isStale()) return;
    const docs = (res && res.data) || res || [];
    if (!Array.isArray(docs) || docs.length === 0) {
      debugLog('[xsync] no linked document for cml', cmlId);
      return;
    }
    // The slim getByCmlId item reliably carries only `id`; resolve the FULL doc
    // first (same as CmlPopupActionBar.openDoc), then compare identity + open.
    const fullDoc = await GET_DOCUMENT_BY_ID(docs[0].id);
    if (isStale() || !fullDoc) return;
    const active = store.getters['getActiveAttachment'];
    if (active && fullDoc.documentId && String(getDocumentId(active)) === String(fullDoc.documentId)) {
      syncDocHighlight('cml', cmlId, colorHex); // already open — just (re)highlight
      return;
    }
    const forgeAttachment = await store.dispatch('GET_DOCUMENT_BY_REFERENCE_ID', {
      documentId: fullDoc.documentId,
      storageReference: fullDoc.storageReference,
      name: fullDoc.fileName,
    });
    if (isStale()) return;
    // Swap the (already-open) doc viewer to the CML-linked document. openAttachment is
    // the shared loader every doc-open caller uses; we only reach here when a doc viewer
    // is already in use (focusCmlDoc gate), so this is a content swap, not a panel
    // create. The isStale checks above bail early on a rapid re-select during the
    // fetches; the swap itself is last-wins (a newer open's finish() supersedes).
    openAttachment(forgeAttachment);
    debugLog('[xsync] swapped doc viewer to linked document for cml', cmlId, 'docId=', docs[0].id);
    assertDocEntityHighlightWhenRendered({
      entityType: 'cml',
      entityId: cmlId,
      colorHex,
      targetDocumentId: fullDoc.documentId,
      isStale,
    });
  } catch (err) {
    debugLog('[xsync] openCmlLinkedDocument failed', err);
  }
}

/**
 * The ONE colour a CML/TmlPoint highlights with, across every viewer.
 *
 * Prefer the 3D marker's own material (it was built from the condition colour) and fall back to
 * the row's condition rating, so 360 and the document match even with no marker rendered.
 * `buildTmlPopupViewModel` copies a TmlPoint's parent CML rating onto `raw.conditionRating` (D14),
 * so the same resolver is correct for both kinds; it returns NO_DATA grey rather than null for a
 * missing rating, which is why the viewers never diverge to green / link-type.
 *
 * Plan C (Task 5): this used to exist verbatim in three places — `syncOnLoaded`,
 * `useFocusCml.focusInOpenViewers` and `CmlPopupActionBar.resolveEntityHighlightColor`. One
 * resolver, one answer.
 */
export function resolveEntityHighlightColor({
  entityType = 'cml',
  entityId = null,
  raw = null,
  marker,
} = {}) {
  const fs = _fs();
  const found = marker !== undefined
    ? marker
    : (fs && fs.find3DMarkerByEntity ? fs.find3DMarkerByEntity(entityType, entityId) : null);

  if (found && found.material && found.material.color) {
    return '#' + found.material.color.getHexString();
  }
  if (fs && fs._getCmlConditionColor && raw) {
    try { return fs._getCmlConditionColor(raw); } catch (err) { return null; }
  }
  return null;
}

/**
 * The ONE "focus this CML/TmlPoint" path. Every CML surface goes through it: the Monitoring row,
 * the tree, the list, the kebab "View on 3D", the popup action bar, and a 3D/360 marker click.
 * A passive 3D popup Loaded edge may retain a non-CML scene already published in that viewer;
 * explicit row/kebab/doc/360 focus and CML-to-CML replacement still author a new entity scene.
 *
 * 3D is NOT decided here. The scene — isolation, gray context, the exact target's colour, the
 * camera fit, the pulse and the point marker — is authored as ONE entity scope and handed to
 * `useSpatialFocus.show`. This function owns only what the composable has no channel for:
 *
 *   - the 360 panorama (recentre + hotspot highlight);
 *   - the document viewer (link highlight, or a swap to the CML-linked document);
 *   - the LOCKED-pair camera coupling, which physically overrides an independent 3D fit.
 *
 * Camera precedence for those retained channels:
 *
 *   locked + the click came from INSIDE the 3D/360 pair
 *       → no coupled move; the pair already frames the same region.
 *   locked, any other source
 *       → ONE coupled `zoomToPointIn3DAnd360` re-centre (+ rotate the 360), AFTER the render, so it
 *         wins. While the 3D camera is slaved to the panorama's scan vantage an independent fit
 *         cannot hold, and two separate moves fight each other.
 *   unlocked
 *       → the composable owns the 3D camera; only a non-source 360 is recentred here.
 *
 * @returns {{marker: object|null, colorHex: string|null, has360: boolean, locked: boolean,
 *            status: string, markerRefused: string|null, markerRendered: number,
 *            markerVisible: boolean}|null}
 */
export async function focusEntityAcrossViewers({
  entityType,
  entityId,
  raw = null,
  rawPoint = null,
  sourceViewer = null,
  preserveSpatialScene = false,
  swapDocument = false,
  title = null,
} = {}) {
  const fs = _fs();
  if (!fs) return null;

  const viewer = store.state.viewer;
  const scopeKind = ENTITY_SCOPE_KIND[entityType] || null;
  const has360 = !!fs.panoramaViewer && !!store.state.selectedImage;
  // Real reverse-sync gate (mainExtension.js): both flags, not areViewsLocked alone.
  const locked = !!store.state.areViewsLocked
    && !!(store.state.selectedImage && store.state.selectedImage.isLocked);
  const spatialFocus = useSpatialFocus();
  const activeScene = viewer ? spatialFocus.getActiveScene({ viewer }) : null;
  // Inspection Info replaces the shared point-marker layer without replacing the still-published
  // dashboard element scene. A native 3D marker popup is passive in that state: keep the existing
  // non-CML owner. A prior CML entity focus is different — the next native marker must supersede it.
  const hasPublishedNonCmlScene = sourceViewer === '3d'
    && !!activeScene
    && activeScene.owner?.widgetId !== CML_FOCUS_OWNER_ID;
  const retainSpatialScene = sourceViewer === '3d'
    && (preserveSpatialScene === true || hasPublishedNonCmlScene);

  // Resolve the colour BEFORE the render: the existing marker's material is the preferred source
  // and the render may legitimately replace the marker layer.
  const marker = fs.find3DMarkerByEntity(entityType, entityId); // null if 3D closed / not present
  const colorHex = resolveEntityHighlightColor({ entityType, entityId, raw, marker });
  const rating = raw && raw.conditionRating !== undefined && raw.conditionRating !== null
    ? raw.conditionRating
    : null;

  let focusResult = null;
  if (viewer && scopeKind && entityId && !retainSpatialScene) {
    try {
      focusResult = await spatialFocus.show({
        owner: { widgetId: CML_FOCUS_OWNER_ID, title: title || String(entityId) },
        scope: {
          kind: 'entities',
          entities: [rating === null
            ? { kind: scopeKind, id: entityId }
            : { kind: scopeKind, id: entityId, encodingValue: rating }],
        },
        // One value, one colour — the same shared resolver the 360 and the document use, so the
        // three viewers can never disagree about this entity's condition colour.
        encoding: colorHex
          ? { column: 'conditionRating', displayName: 'Condition', colorFor: () => colorHex }
          : null,
        viewer,
        // Swap straight from the previous focus to this CML, like a plain tag-card isolate: the
        // whole model never flashes in between, and the user's ghosting is kept.
        swapInPlace: true,
      });
    } catch (err) {
      debugLog('[xsync] spatial focus failed', err);
    }
  }

  const receiptMarkers = (focusResult && focusResult.receipt && focusResult.receipt.markers) || null;
  // The render may have replaced the marker layer, which invalidates the mesh captured above.
  const liveMarker = fs.find3DMarkerByEntity(entityType, entityId) || marker;

  try {
    if (locked && (sourceViewer === '3d' || sourceViewer === '360')) {
      debugLog('[xsync] locked + source in 3D/360 pair — skip coupled move (visible in both)');
    } else if (locked) {
      if (liveMarker && liveMarker._basePosition) fs.zoomToPointIn3DAnd360(liveMarker._basePosition);
      if (has360 && rawPoint) fs.zoomToPointIn360Viewer(rawPoint);
      debugLog('[xsync] locked + source', sourceViewer, '— re-center locked pair');
    } else if (has360 && rawPoint && sourceViewer !== '360') {
      fs.zoomToPointIn360Viewer(rawPoint);
    }
  } catch (err) {
    debugLog('[xsync] camera sync skipped', err);
  }

  // Highlights — every primitive self-guards on its own viewer, so the source highlight is
  // harmless reinforcement.
  try { if (liveMarker) fs.highlightMarkerIn3D(liveMarker); } catch (err) { debugLog('[xsync] 3D highlight skipped', err); }
  try { if (has360 && rawPoint) fs.highlightPointIn360(rawPoint, colorHex, entityType); } catch (err) { debugLog('[xsync] 360 highlight skipped', err); }
  try { focusCmlDoc(entityType, entityId, colorHex, swapDocument); } catch (err) { debugLog('[xsync] doc focus skipped', err); }

  _active = { entityType, entityId, rawPoint, colorHex };

  // The marker channel is SHARED, so a refusal is normal rather than an error: `foreignMarkerLayer`
  // means somebody else's set is already on screen (this entity's marker is very likely in it) and
  // `cmlMarkersHidden` means the user's own display toggle is off. Reported, never hidden — a
  // caller with UI surfaces it only when the entity ends up with no visible point at all.
  const markerRefused = receiptMarkers ? receiptMarkers.refused : null;
  const outcome = {
    marker: liveMarker,
    colorHex,
    has360,
    locked,
    status: focusResult ? focusResult.status : retainSpatialScene ? 'retained' : 'unavailable',
    markerRefused,
    markerRendered: receiptMarkers ? receiptMarkers.rendered : 0,
    markerVisible: !!liveMarker || !!(receiptMarkers && receiptMarkers.rendered > 0),
  };
  debugLog('[xsync] focused', entityType, entityId, 'status=', outcome.status, 'has360=', has360, 'locked=', locked, 'source=', sourceViewer, 'markerRefused=', markerRefused);

  return outcome;
}

/** Orient + highlight the non-source (unlocked) or paired (locked) viewers. */
function syncOnLoaded() {
  const fs = _fs();
  if (!fs) return;

  const {
    entityType,
    entityId,
    sourceViewer,
    preserveSpatialScene,
    vm,
  } = popupCardState;
  if (entityType !== 'cml' && entityType !== 'tmlpoint') return;

  const raw = vm && vm.raw ? vm.raw : null;

  // Reverse cross-viewer sync → the Monitoring tab is another surface. Reflect the
  // popup's entity as the selected CML/TML so its row expands + highlights (the mirror
  // of the forward row→3D path). A 3D/360 marker click opens the popup but NEVER
  // committed this, so the tab stayed inert on a marker click (proven via console:
  // `[xsync] synced cml` fires, no `[MonitoringTab] selectedCmlId`). Committing here
  // wires it without touching the audited marker handler. Non-intrusive: if the CML
  // isn't in the currently-open master tag's list, applySelectedCmlHighlight just skips
  // — we deliberately do NOT force-open another panel (that stays the "View Detail"
  // action). Mirrors REQUEST_VIEW_DETAIL's dual commit (clear the TML row for a CML).
  try {
    if (entityType === 'cml') {
      store.commit('SET_SELECTED_CML_ID', entityId, { root: true });
      store.commit('SET_SELECTED_TML_POINT_ID', null, { root: true });
    } else if (entityType === 'tmlpoint') {
      const parentCmlId = vm && vm.cmlRecordId ? vm.cmlRecordId : null;
      if (parentCmlId) store.commit('SET_SELECTED_CML_ID', parentCmlId, { root: true });
      store.commit('SET_SELECTED_TML_POINT_ID', entityId, { root: true });
    }
  } catch (err) { debugLog('[xsync] tab selection sync skipped', err); }

  // Entity scope through the one composable, then the retained 360/document channels. Fire and
  // forget: this runs from the popup-state watcher, and the composable's own per-viewer generation
  // already makes a rapid re-select deterministic.
  focusEntityAcrossViewers({
    entityType,
    entityId,
    raw,
    rawPoint: raw && raw.point ? raw.point : null,
    sourceViewer,
    preserveSpatialScene,
    swapDocument: false,
    title: (vm && (vm.cmlNo || vm.positionLabel)) || null,
  }).catch((err) => debugLog('[xsync] focus failed', err));
}

// Lazily instantiated singleton watcher — started from MainView's created() hook,
// NOT at import time. A top-level `new Vue` would run during the store's own
// circular-init chain (store → forgeUtils → forgeService → forgeviewer → router →
// MainView → this module); the watcher's eager computed eval would then read an
// undefined `store` and throw. Deferring to created() guarantees the store is
// ready, so the watchers track real reactive deps. Idempotent — a MainView
// remount reuses the existing instance.
let _host = null;

export function startCrossViewerSync() {
  if (_host) return _host;
  _host = new Vue({
    computed: {
      // Single derived key → one reaction per (open, status, entity) transition,
      // instead of several field watchers racing through multiple cleanup passes.
      syncKey() {
        return `${popupCardState.isOpen}:${popupCardState.status}:${popupCardState.entityType}:${popupCardState.entityId}`;
      },
      selectedImageId() {
        return store.state.selectedImage ? store.state.selectedImage.id : null;
      },
    },
    watch: {
      syncKey() {
        clearSync(); // idempotent: drop any prior highlight before (maybe) applying a new one
        if (popupCardState.isOpen && popupCardState.status === 'Loaded') syncOnLoaded();
      },
      // A 360 scan change rebuilds hotspots (wiping the shared highlight slot) while
      // the popup stays open; re-assert the highlight on the new scan.
      selectedImageId() {
        if (_active && _active.rawPoint && popupCardState.isOpen) {
          Vue.nextTick(() => {
            const fs = _fs();
            if (!fs) return;
            // Re-assert the highlight AND recenter — a fresh 360 launch (focusCml)
            // or a scan switch rebuilds hotspots; mirror tag-zoom by also moving
            // the new scan's camera to the entity (highlight alone left it off-center).
            try { fs.highlightPointIn360(_active.rawPoint, _active.colorHex, _active.entityType); } catch (err) { debugLog('[xsync] 360 highlight re-assert skipped', err); }
            if (popupCardState.sourceViewer !== '360') {
              try { fs.zoomToPointIn360Viewer(_active.rawPoint); } catch (err) { debugLog('[xsync] 360 zoom re-assert skipped', err); }
            }
          });
        }
      },
    },
  });
  return _host;
}
